import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldErrors, FieldPath, UseFormReturn } from 'react-hook-form';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { CheckIcon } from 'lucide-react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
  Form,
  FormField,
  Button,
  MobileNavBar,
  useModalControls,
  type FieldOption,
} from '@noor/ui';
import { getApiErrorMessage, getApiFieldErrors, isApiError } from '@noor/api';
import {
  confirmDialog,
  toast,
  useFormSeed,
  MAX_IMAGE_LABEL,
  type ModalRenderProps,
} from '@noor/lib';
import { cn } from '@noor/lib/cn';
import { useMenuProduct, type MenuProduct } from '@/entities/menu';
import {
  DESCRIPTION_MAX_LENGTH,
  IKPU_CODE_LENGTH,
  NAME_MAX_LENGTH,
  type ProductFormImage,
  type ProductFormValues,
} from '../model/schema';
import { useUpsertProduct } from '../model/use-upsert-product';
import { useDeleteProduct } from '../model/use-delete-product';
import { useUploadProductImages } from '../model/use-upload-images';
import { buildDefaults, toProductInput } from '../lib/product-form';
import { UNIT_CODES } from '../config/unit-codes';
import { MeasureAmountField } from './measure-amount-field';
import { SellingHoursField } from './selling-hours-field';
import { MeasurementTypeField } from './measurement-type-field';
import { VatPercentField } from './vat-percent-field';
import { ModifierStep } from './modifier-step';
import { RecommendationsSection } from './recommendations-section';
import { MobilePhotoGrid } from './mobile-photo-grid';
import { CompositionNutritionFields } from './composition-nutrition-fields';

const STEPS = ['details', 'data', 'modifiers'] as const;
type Step = (typeof STEPS)[number];

/** Filled gray pill field — the mobile design's one input treatment. */
const FIELD = 'rounded-3xl border-none bg-muted shadow-none';

/** Label→control gap, matching the desktop wizard's tightened spacing. */
const FIELD_GROUP = 'gap-2';

/** The three platform languages, in the order the design lists them. */
const LOCALES = [
  { suffix: 'Ru', lang: 'ru' },
  { suffix: 'Uz', lang: 'uz' },
  { suffix: 'En', lang: 'en' },
] as const;

/** Which fields each tab owns, so advancing validates just that tab. */
const STEP_FIELDS: Record<Step, FieldPath<ProductFormValues>[]> = {
  details: [
    'nameRu',
    'nameUz',
    'nameEn',
    'descriptionRu',
    'descriptionUz',
    'descriptionEn',
    'compositionRu',
    'compositionUz',
    'compositionEn',
    'calories',
    'fats',
    'proteins',
    'categoryId',
    'alwaysReady',
    'isAdult',
    'prepTimeMinutes',
    'availableFrom',
    'availableTo',
  ],
  data: [
    'images',
    'ikpuCode',
    'unitCode',
    'measureAmount',
    'measurementType',
    'vatPercent',
    'hasMarkedCode',
    'markedCode',
  ],
  modifiers: ['modifiersDisabled', 'modifiers'],
};

/**
 * Field label: text, a required marker, and (for localized rows) the language
 * it captures. Localized name/description rows label by language name alone
 * (Figma 2087329211: «Русский»/«Узбекский»/«Английский»), so `children` carries
 * the language and no `tag` is rendered.
 */
function FieldLabel({
  children,
  required = false,
}: {
  children: string;
  required?: boolean;
}) {
  return (
    <FormField.Label className="flex items-center gap-px px-1 text-sm font-medium tracking-tight text-foreground">
      {children}
      {required && <span className="text-primary">*</span>}
    </FormField.Label>
  );
}

type ProductModalMobileProps =
  | ModalRenderProps<'add-product'>
  | ModalRenderProps<'edit-product'>;

/**
 * Add/edit product on mobile (Figma 26609:10817 details / 26609:14140 data /
 * 26612:18208 modifier) — a full-screen native-app takeover, not the desktop
 * dialog rendered narrow. Three tabs over ONE form, same as desktop: a
 * per-tab form is what would let a read landing while the merchant is on the
 * modifier tab silently reset the tabs behind it.
 *
 * Rendered through the bare `Dialog` primitives rather than the shared
 * `Modal` shell — that shell centers a card with a close-X and a backdrop,
 * while this screen IS the backdrop (full-bleed, its own back arrow, no
 * close-X), matching the Figma phone frame instead of a dialog on a phone.
 */
export function ProductModalMobile({
  payload,
  close,
  form,
}: ProductModalMobileProps & { form: UseFormReturn<ProductFormValues> }) {
  const { t } = useTranslation('eats');
  const { open } = useModalControls();
  const upsert = useUpsertProduct();
  const remove = useDeleteProduct();
  const { uploadImages, isUploading } = useUploadProductImages();
  const [step, setStep] = useState<Step>('details');

  const isEditing = payload.mode === 'edit';
  const product = payload.mode === 'edit' ? payload.product : undefined;
  const { categories, defaultCategoryId } = payload;

  // Which tabs a tap can reach right now. Editing an existing product means
  // every tab is already filled with a valid record, so all three open at
  // once — there is nothing to "unlock" by walking forward, and the Figma
  // edit frames show every tab tappable from the start. Creating a NEW
  // product starts with only Детали open: Данные/Модификатор have nothing
  // to show yet, so a tap gets nowhere until "Далее" validates the tab
  // before it. Once a tab opens it stays open — going back is always safe,
  // since every tab shares one form.
  const [unlockedSteps, setUnlockedSteps] = useState<ReadonlySet<Step>>(
    () => new Set(isEditing ? STEPS : ['details']),
  );

  const categoryOptions = useMemo<FieldOption[]>(
    () => categories.map((c) => ({ label: c.name, value: String(c.id) })),
    [categories],
  );
  const unitOptions = useMemo<FieldOption[]>(
    () =>
      UNIT_CODES.map(({ code, labelKey }) => ({
        value: code,
        label: t(`menu.product.form.units.${labelKey}`),
      })),
    [t],
  );

  // The catalog card is a projection — it omits the photo gallery and carries
  // a branch-resolved price — so an edit re-reads the product itself.
  const { data: fetched, dataUpdatedAt } = useMenuProduct(product?.id);

  const source = useMemo<MenuProduct | undefined>(
    () => (product ? { ...product, ...fetched } : undefined),
    [product, fetched],
  );

  // `onSubmit`, not the shared `onTouched` default — same reason as the desktop
  // wizard: this is a three-TAB form, so tabbing through Детали on the way to
  // Данные marked every field touched and painted required-errors under inputs
  // the merchant had not finished with. Validation now runs on «Далее» (which
  // triggers just that tab's fields) and on the final save.
  const { reset } = form;
  useFormSeed(
    true,
    product?.id === undefined ? undefined : String(product.id),
    dataUpdatedAt,
    () => {
      if (!form.formState.isDirty) reset(buildDefaults(source, defaultCategoryId));
    },
  );

  const alwaysReady = form.watch('alwaysReady') ?? false;

  useEffect(() => {
    if (!alwaysReady) return;
    form.setValue('prepTimeMinutes', undefined);
    form.clearErrors('prepTimeMinutes');
  }, [alwaysReady, form]);

  const hasMarkedCode = form.watch('hasMarkedCode') === true;
  const ikpuCode = form.watch('ikpuCode');
  const ikpuLength = typeof ikpuCode === 'string' ? ikpuCode.length : 0;

  useEffect(() => {
    if (hasMarkedCode) return;
    form.setValue('markedCode', '');
    form.clearErrors('markedCode');
  }, [hasMarkedCode, form]);

  const stepIndex = STEPS.indexOf(step);

  async function handleNext(): Promise<void> {
    const fields = STEP_FIELDS[step];
    if (!(await form.trigger(fields))) return;
    const next = STEPS[stepIndex + 1];
    if (!next) return;
    setUnlockedSteps((current) => new Set(current).add(next));
    setStep(next);
  }

  function handleBack(): void {
    const previous = STEPS[stepIndex - 1];
    if (previous) setStep(previous);
    else close();
  }

  function handleTabSelect(next: Step): void {
    // A locked tab (create mode, not yet reached) ignores the tap rather than
    // navigating — its fields haven't validated, so landing there would show
    // an empty/invalid screen with no indication of what's still missing back
    // on the tab before it.
    if (unlockedSteps.has(next)) setStep(next);
  }

  function handleInvalid(errors: FieldErrors<ProductFormValues>): void {
    // Diagnostic: log the failing paths so a "dead" save can be traced.
    console.error('[product-modal] save blocked, validation errors:', errors);
    const errorRoots = new Set(Object.keys(errors));
    const invalidStep = STEPS.find((candidate) =>
      STEP_FIELDS[candidate].some((field) =>
        errorRoots.has(String(field).split('.')[0] ?? field),
      ),
    );
    if (invalidStep) {
      setUnlockedSteps((current) => new Set(current).add(invalidStep));
      setStep(invalidStep);
    }
  }

  async function handleSubmit(values: ProductFormValues): Promise<void> {
    let images: ProductFormImage[];
    let uploadFailed = false;
    let uploadError: unknown;
    try {
      images = await uploadImages(values.images);
    } catch (error) {
      uploadFailed = true;
      uploadError = error;
      images = (values.images ?? []).filter((image) => !image.file);
    }

    const savedImageCount = source?.imageUrls?.length ?? (source?.imageUrl ? 1 : 0);
    const hadSavedImages = savedImageCount > 0;
    const input = toProductInput(
      values,
      images,
      hadSavedImages,
      savedImageCount,
      // The cover's gallery id — see the desktop wizard for why an index cannot
      // detect a re-star.
      source?.imageIds?.[source.coverIndex ?? 0],
      source?.modifiersDisabled ?? false,
      { from: source?.availableFrom, to: source?.availableTo },
      source,
    );

    // The editor does not choose branches. Nothing on the wire reports which
    // branches stock a product, so this is `undefined` on an edit and the
    // update touches only the overlay of the branch being viewed — see
    // `menuApi.updateProduct`.
    const existingBranchIds = source?.branchIds;

    upsert.mutate(
      product === undefined
        ? input
        : {
            ...input,
            id: product.id,
            ...(existingBranchIds === undefined ? {} : { branchIds: existingBranchIds }),
          },
      {
        onSuccess: (outcome) => {
          // Confirm every successful save — see the desktop wizard for why the
          // silent close was ambiguous, and why a moderation-queued edit gets
          // its own wording rather than claiming the save landed.
          toast.success(
            outcome.status === 'pending'
              ? t('menu.product.form.pendingReview')
              : isEditing
                ? t('menu.product.form.successEdit')
                : t('menu.product.form.success'),
          );
          if (uploadFailed) {
            toast.error(
              getApiErrorMessage(uploadError, t('menu.product.form.uploadErrorSaved')),
            );
          }
          close();
        },
        onError: (error) => {
          const { reason } = getApiFieldErrors(error);
          const fallback =
            reason ??
            (isApiError(error) && error.message?.trim()
              ? error.message
              : t('menu.product.form.error'));
          toast.error(getApiErrorMessage(error, fallback));
        },
      },
    );
  }

  async function handleDelete(): Promise<void> {
    if (product === undefined) return;

    await confirmDialog({
      title: t('menu.product.deleteConfirm.title'),
      description: t('menu.product.deleteConfirm.description', { name: product.name }),
      confirmLabel: t('menu.product.deleteConfirm.confirm'),
      cancelLabel: t('menu.product.deleteConfirm.cancel'),
      destructive: true,
      onConfirm: () =>
        remove.mutateAsync(product.id, {
          onSuccess: () => {
            toast.success(t('menu.product.deleteSuccess'));
            close();
          },
          onError: (error) =>
            toast.error(getApiErrorMessage(error, t('menu.product.deleteError'))),
        }),
    });
  }

  const isLastStep = step === 'modifiers';

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogPortal>
        <DialogOverlay className="bg-card" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-50 flex flex-col overflow-hidden bg-card',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Visually-hidden — the screen states its own title via
           * `MobileNavBar`; Radix still requires a `DialogTitle` for a11y. */}
          <DialogTitle className="sr-only">
            {t(isEditing ? 'common:modal.edition' : 'common:modal.creation')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t(isEditing ? 'common:modal.edition' : 'common:modal.creation')}
          </DialogDescription>

          <MobileNavBar
            className="shrink-0"
            title={t(isEditing ? 'common:modal.edition' : 'common:modal.creation')}
            leading={
              <MobileNavBar.Back
                onClick={handleBack}
                aria-label={t('common:actions.back')}
              />
            }
          />

          <Form
            form={form}
            onSubmit={handleSubmit}
            onInvalid={handleInvalid}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex min-h-0 flex-1 flex-col gap-0">
              {/* Figma 26609:14304/26612:18242 — a plain gray track (no
               * border), an active tab that lifts to a solid white pill, a
               * completed tab that stays flat but turns orange with a small
               * check under the label, and a locked tab that stays flat and
               * muted. Three visual states rather than the shared `Tabs`
               * component's two (active/inactive), so this is hand-rolled
               * rather than bent onto that primitive's variants — plain
               * buttons with the ARIA tablist pattern, since Radix's Tabs
               * lives one layer down in `@noor/ui` and isn't a direct
               * dependency here. */}
              <div
                role="tablist"
                className="mx-4 mt-4 flex items-center justify-center gap-0 rounded-[60px] bg-muted p-0.5"
              >
                {STEPS.map((tabStep) => {
                  const isActive = tabStep === step;
                  const isUnlocked = unlockedSteps.has(tabStep);
                  const isCompleted = isUnlocked && !isActive;
                  return (
                    <button
                      key={tabStep}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      disabled={!isUnlocked}
                      onClick={() => handleTabSelect(tabStep)}
                      className={cn(
                        'flex flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-[60px] px-4 py-2.5 text-sm font-medium tracking-tight transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed',
                        isActive && 'bg-card text-foreground',
                        isCompleted && 'text-primary',
                        !isActive && !isCompleted && 'text-muted-foreground',
                      )}
                    >
                      {t(`menu.product.form.steps.${tabStep}`)}
                      {isCompleted && <CheckIcon className="size-4" aria-hidden />}
                    </button>
                  );
                })}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-6">
                <div className={step === 'details' ? 'flex flex-col gap-5' : 'hidden'}>
                  {LOCALES.map(({ suffix, lang }) => (
                    <FormField
                      key={suffix}
                      name={`name${suffix}`}
                      className={FIELD_GROUP}
                    >
                      <FieldLabel required>{t(`utilities.languages.${lang}`)}</FieldLabel>
                      <FormField.Input
                        className={FIELD}
                        maxLength={NAME_MAX_LENGTH}
                        placeholder={t(`menu.product.form.namePlaceholder${suffix}`)}
                      />
                      <FormField.Message />
                    </FormField>
                  ))}

                  {LOCALES.map(({ suffix, lang }) => (
                    <FormField
                      key={suffix}
                      name={`description${suffix}`}
                      className={FIELD_GROUP}
                    >
                      <FieldLabel required>{t(`utilities.languages.${lang}`)}</FieldLabel>
                      <FormField.TextArea
                        className={cn('min-h-20', FIELD)}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        placeholder={t(
                          `menu.product.form.descriptionPlaceholder${suffix}`,
                        )}
                      />
                      <FormField.Message />
                    </FormField>
                  ))}

                  <CompositionNutritionFields fieldClassName={FIELD} />

                  {/* No branch picker — the desktop wizard has none either.
                   * Which branches stock a product is a per-branch overlay
                   * (`RestaurantProduct`) managed from the branch's own menu,
                   * and nothing on the wire reports the current set, so the
                   * control could only ever seed EMPTY. On an EDIT that read as
                   * "this product is sold nowhere", inviting the merchant to
                   * re-pick branches the submit then ignores. Create no longer
                   * sends overlays in the product POST body. */}

                  <FormField name="categoryId" className={FIELD_GROUP}>
                    <FieldLabel>{t('menu.product.form.categoryLabel')}</FieldLabel>
                    <FormField.Select
                      className={FIELD}
                      options={categoryOptions}
                      placeholder={t('menu.product.form.categoryPlaceholder')}
                    />
                    <FormField.Message />
                  </FormField>

                  {/* Price for the branch being edited — see the desktop
                   * dialog's note on why it is a per-branch overlay and why
                   * an empty box is omitted rather than sent as 0. */}
                  <FormField name="price" className={FIELD_GROUP}>
                    <FieldLabel>{t('menu.product.form.priceLabel')}</FieldLabel>
                    <FormField.Price
                      className={FIELD}
                      placeholder={t('menu.product.form.pricePlaceholder')}
                      suffix={t('menu.product.form.currencySuffix')}
                    />
                    <FormField.Message />
                  </FormField>

                  <FormField name="isAdult" className="py-1">
                    <FormField.Switch label={t('menu.product.form.isAdult')} />
                  </FormField>

                  <FormField name="alwaysReady" className="py-1">
                    <FormField.Checkbox
                      label={t('menu.product.form.alwaysReady')}
                      className="[&_label]:text-sm [&_label]:font-medium [&_label]:tracking-tight [&_label]:text-foreground"
                    />
                  </FormField>

                  {alwaysReady ? (
                    <p className="px-1 text-xs leading-relaxed text-muted-foreground">
                      {t('menu.product.form.alwaysReadyHint')}
                    </p>
                  ) : (
                    <FormField name="prepTimeMinutes" className={FIELD_GROUP}>
                      <FieldLabel required>
                        {t('menu.product.form.prepTimeLabel')}
                      </FieldLabel>
                      <FormField.Number
                        className={FIELD}
                        min={1}
                        max={120}
                        placeholder={t('menu.product.form.prepTimePlaceholder')}
                        suffix={t('menu.product.form.prepTimeSuffix')}
                      />
                      <FormField.Message />
                    </FormField>
                  )}

                  <SellingHoursField
                    className="flex flex-col gap-2"
                    fieldClassName={FIELD}
                  />

                  {/* No price box, in EITHER mode — price is a per-branch
                   * overlay set from the branch's own menu, and requiring it
                   * here blocked saving a name fix on a product whose branch
                   * price was not set yet. Same rule as the desktop wizard. */}
                </div>

                <div className={step === 'data' ? 'flex flex-col gap-5' : 'hidden'}>
                  <FormField name="images">
                    <MobilePhotoGrid
                      name="images"
                      uploadLabel={t('menu.product.form.uploadPhoto')}
                      hintLabel={t('menu.product.form.mobilePhotoHint')}
                      removeLabel={t('menu.product.form.removePhoto')}
                      onRejected={(names) =>
                        toast.error(
                          t('menu.product.form.photoTooLarge', {
                            name: names.join(', '),
                            limit: MAX_IMAGE_LABEL,
                          }),
                        )
                      }
                    />
                    <FormField.Message />
                  </FormField>

                  <FormField name="ikpuCode" className={FIELD_GROUP}>
                    <FieldLabel required>{t('menu.product.form.ikpuLabel')}</FieldLabel>
                    <FormField.Masked
                      mask={'#'.repeat(IKPU_CODE_LENGTH)}
                      className={FIELD}
                      placeholder={t('menu.product.form.ikpuPlaceholder')}
                    />
                    <div className="flex items-center justify-between">
                      <FormField.Message />
                      <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                        {ikpuLength}/{IKPU_CODE_LENGTH}
                      </span>
                    </div>
                  </FormField>

                  <FormField name="unitCode" className={FIELD_GROUP}>
                    <FieldLabel required>
                      {t('menu.product.form.unitCodeLabel')}
                    </FieldLabel>
                    <FormField.Select
                      className={FIELD}
                      options={unitOptions}
                      placeholder={t('menu.product.form.unitCodePlaceholder')}
                    />
                    <FormField.Message />
                  </FormField>

                  <MeasureAmountField className={FIELD_GROUP} fieldClassName={FIELD} />

                  <MeasurementTypeField className={FIELD_GROUP} fieldClassName={FIELD} />

                  <VatPercentField className={FIELD_GROUP} fieldClassName={FIELD} />

                  <FormField name="hasMarkedCode" className="py-1">
                    <FormField.Switch label={t('menu.product.form.markingLabel')} />
                  </FormField>

                  {hasMarkedCode && (
                    <FormField name="markedCode" className={FIELD_GROUP}>
                      <FieldLabel>{t('menu.product.form.markedCodeLabel')}</FieldLabel>
                      <FormField.Input
                        className={FIELD}
                        autoComplete="off"
                        placeholder={t('menu.product.form.markedCodePlaceholder')}
                      />
                      <FormField.Message />
                    </FormField>
                  )}
                </div>

                <div className={step === 'modifiers' ? 'block' : 'hidden'}>
                  <ModifierStep />
                  {isEditing && product ? (
                    <RecommendationsSection productId={String(product.id)} />
                  ) : null}
                </div>
              </div>
            </div>

            <Form.RootError className="px-4" />

            {/* Sticky footer, safe-area aware — the mobile design's Далее/
             * Сохранить pill, plus a destructive delete link on the details
             * tab of an edit. («Добавить группу» lives inside `ModifierStep`
             * itself, not here — it is a modifier-tab action, not a footer
             * action shared across tabs.) */}
            <div className="flex shrink-0 flex-col gap-2 border-t border-border bg-card px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
              {isEditing && step === 'details' && (
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={remove.isPending}
                  className="self-center pb-1 text-sm font-semibold tracking-tight text-destructive disabled:opacity-60"
                >
                  {t('menu.product.form.deleteProduct')}
                </button>
              )}

              {isLastStep ? (
                <Button
                  key="submit"
                  type="submit"
                  size="lg"
                  className="h-14 w-full rounded-full text-base"
                  disabled={upsert.isPending || isUploading}
                >
                  {isUploading
                    ? t('menu.product.form.uploading')
                    : t('common:actions.save')}
                </Button>
              ) : (
                <Button
                  key="continue"
                  type="button"
                  size="lg"
                  className="h-14 w-full rounded-full text-base"
                  onClick={() => void handleNext()}
                >
                  {t('common:actions.next')}
                </Button>
              )}
            </div>
          </Form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
